/** Планирование маршрута: старт → цель (юнит/POI) по графу RVF. */
import { densifyPolyline, haversineM, type LonLat, polylineLengthM } from '@app/geo'
import type { BuiltGraph, RouteProfile } from './graph'
import { arrivalLegs } from './route-access'
import { foreignDoorIds, foreignFootprints } from './route-foreign'
import { pointInAnyRing, segmentCrossesRings } from './route-geom'
import {
  bestEdgeExit,
  networkDistances,
  OFF_NETWORK_COST_FACTOR,
  parkAndWalkRoute,
  sequentialLegs,
  targetLevelOf,
  VIA_MIN_M,
} from './route-legs'
import { type RouteLevelSplit, splitByLevel } from './route-level-legs'
import { arrivalPoint, entranceAimPoint, undrawnEntrancePoint } from './route-points'
import type { Venue } from './types'

export { type ArrivalLeg, arrivalLegs } from './route-access'
export { targetNodeIds } from './route-points'

/**
 * Скорости по профилю: пешеход с запасом на ворота/двери; коляска чуть медленнее;
 * транспорт — дворовый лимит ~15 км/ч.
 */
export const SPEED_MPS: Record<RouteProfile, number> = {
  foot: 1.3,
  wheelchair: 1.2,
  vehicle: 4.2,
  truck: 3.0,
}
/** Средняя пешая скорость (алиас для вызовов без профиля). */
export const WALKING_SPEED_MPS = SPEED_MPS.foot
/** Шаг денсификации полилинии — требование MapMatcher (матчинг к вершинам). */
export const DENSIFY_STEP_M = 8
/** Слипшиеся вершины (якорь на узле графа) рождают фантомные повороты «0 м». */
const DUPLICATE_POINT_EPS_M = 0.5
/** Дальше этого от сети — сегмент считается «вне дорог» (рисуется пунктиром). */
const OFF_NETWORK_EPS_M = 3
/** Штраф прямому плечу, срезающему ЧУЖОЙ корпус (м-эквивалент): маршрут
 *  «сквозь здание» допустим только когда иначе цели не достичь вовсе. */
const THROUGH_BUILDING_PENALTY_M = 500

export type StartSource = 'anchor' | 'geo' | 'entrance'

/** Строгий режим доступа дорог площадки (дефолт — инклюзивный, см. VenueMeta). */
export function strictRouting(venue: Venue): boolean {
  return venue.venue.routing?.implied_access === false
}

export interface RouteStart {
  readonly point: LonLat
  readonly nodeId: string
  readonly source: StartSource
}

export interface RoutePlan {
  readonly targetId: string
  /** Денсифицированная полилиния от старта до двери цели. */
  readonly polyline: readonly LonLat[]
  /** Часть маршрута ПО дорожной сети / «драйв» до парковки (сплошная линия). */
  readonly networkPolyline: readonly LonLat[]
  /** Пеший «дожим» park-and-walk: от парковки до двери (+внутрь) — пунктир. */
  readonly walkPolyline: readonly LonLat[]
  /** Подход от старта до сети вне дорог (пунктир); пуст, если старт на сети. */
  readonly approach: readonly LonLat[]
  /** Отход от сети до двери цели вне дорог (пунктир); пуст, если дверь на сети. */
  readonly egress: readonly LonLat[]
  /** Путь по узлам графа (уровни/коннекторы — для поэтажной отрисовки). */
  readonly nodeIds: readonly string[]
  /** Поэтажные плечи и переходы (route-level-legs.ts): рисуется ярко только
   *  плечо активного этажа — плоская линия двух этажей выглядит петлёй. */
  readonly levelSplit: RouteLevelSplit
  readonly distanceM: number
  readonly durationS: number
  readonly profile: RouteProfile
  readonly start: RouteStart
}

/** Ближайший узел графа; с level — только на этом уровне (иначе по всем). */
function nearestNodeId(venue: Venue, point: LonLat, level?: string): string | null {
  let best: { id: string; dist: number } | null = null
  for (const node of venue.network.nodes) {
    if (level !== undefined && node.level !== level) continue
    const dist = haversineM(node.point, point)
    if (!best || dist < best.dist) best = { id: node.id, dist }
  }
  if (!best && level !== undefined) return nearestNodeId(venue, point)
  return best?.id ?? null
}

function startFromAnchor(
  venue: Venue,
  anchorId: string | undefined,
  source: StartSource,
): RouteStart | null {
  const anchor = anchorId ? venue.anchors.find((a) => a.id === anchorId) : venue.anchors[0]
  if (!anchor) return null
  const nodeId = anchor.node ?? nearestNodeId(venue, anchor.point, anchor.level)
  return nodeId ? { point: anchor.point, nodeId, source } : null
}

/** Старт: QR-якорь → геопозиция → главный вход (первый якорь / узел-gate). */
export function resolveStart(
  venue: Venue,
  opts: { anchorId?: string | undefined; geo?: LonLat | undefined },
): RouteStart | null {
  if (opts.anchorId) {
    const byAnchor = startFromAnchor(venue, opts.anchorId, 'anchor')
    if (byAnchor) return byAnchor
  }
  if (opts.geo) {
    const nodeId = nearestNodeId(venue, opts.geo)
    if (nodeId) return { point: opts.geo, nodeId, source: 'geo' }
  }
  const byFirstAnchor = startFromAnchor(venue, undefined, 'entrance')
  if (byFirstAnchor) return byFirstAnchor
  // Ни якоря, ни геолокации: стартуем от ГЛАВНОГО входа, а не от «первого
  // узла в файле» — тот мог оказаться любым коридором в глубине здания, и
  // маршрут выглядел бессмыслицей (разбор Pelm, 31.07). Порядок: главный вход
  // (primary) → любой вход → ворота → первый узел (последний резерв).
  const nodes = venue.network.nodes
  const fallback =
    nodes.find((n) => n.kind === 'entrance' && n.attrs?.['primary'] === true) ??
    nodes.find((n) => n.kind === 'entrance') ??
    nodes.find((n) => n.kind === 'gate') ??
    nodes[0]
  return fallback ? { point: fallback.point, nodeId: fallback.id, source: 'entrance' } : null
}

// Выбор точек доступа/прибытия — route-access.ts; граф-плечи — route-legs.ts.

/**
 * Точка цели для финального плеча вне сети (фолбэк): здание → его ДВЕРЬ-вход
 * (даже для арендатора внутри), POI-снаружи → своя точка. Сеть выходит у входа,
 * а не срезает к точке цели.
 */
function targetPointOf(venue: Venue, targetId: string, profile: RouteProfile): LonLat | null {
  return entranceAimPoint(venue, targetId, profile)
}

/**
 * Узел схода с сети для фолбэка: минимум «сеть + прямая до цели». Двери и
 * контуры чужих зданий исключены, а прямое плечо, срезающее чужой корпус,
 * штрафуется — маршрут «сквозь здание» берётся только если иначе никак.
 */
function bestExitNode(
  venue: Venue,
  graph: BuiltGraph,
  dist: ReadonlyMap<string, number>,
  opts: {
    targetId: string
    targetPoint: LonLat
    foreign: ReadonlySet<string>
    foreignRings: readonly number[][][]
    fallbackId: string
  },
): { id: string; cost: number } {
  let bestId = opts.fallbackId
  let bestCost = Infinity
  for (const [id, d] of dist) {
    const node = graph.nodes.get(id)
    if (!node || opts.foreign.has(id) || pointInAnyRing(node.point, opts.foreignRings)) continue
    // Целимся в ТУ ЖЕ точку, что нарисует planRoute: у здания без двери это
    // ближайшая точка его стены (undrawnEntrancePoint), иначе — точка прибытия.
    const aim = undrawnEntrancePoint(venue, opts.targetId, node.point) ?? opts.targetPoint
    const crossing = segmentCrossesRings(node.point, aim, opts.foreignRings)
    const cost =
      d +
      OFF_NETWORK_COST_FACTOR * haversineM(node.point, aim) +
      (crossing ? THROUGH_BUILDING_PENALTY_M : 0)
    if (cost < bestCost) {
      bestCost = cost
      bestId = id
    }
  }
  return { id: bestId, cost: bestCost }
}

/**
 * Фолбэк, когда ни одна дверь цели не достижима по сети (дорога не дорисована,
 * узел цели в несвязанном куске): ведём по сети до узла, минимизирующего
 * «сеть + напрямую», при выгоде — сход с середины ребра (via). Как «частично
 * пешком» в уличных навигаторах: маршрут честный и никогда не пропадает молча.
 */
function reachableFallback(
  venue: Venue,
  graph: BuiltGraph,
  start: RouteStart,
  targetId: string,
  profile: RouteProfile,
): { nodeIds: string[]; targetPoint: LonLat; via: LonLat | null } | null {
  const targetPoint = targetPointOf(venue, targetId, profile)
  if (!targetPoint) return null
  const strict = strictRouting(venue)
  const { dist, prev } = networkDistances(graph, start.nodeId, profile, strict)
  // Двери ЧУЖИХ зданий — не точка схода: иначе маршрут «заезжает» в соседний
  // корпус (фидбек владельца 27.07: SKL1k вёл через дверь «Склада B»).
  const foreign = foreignDoorIds(venue, targetId)
  // Контуры чужих зданий: ни узел схода, ни «поворот» на ребре не должны быть
  // внутри них — иначе линия визуально въезжает в соседний корпус.
  const foreignRings = foreignFootprints(venue, targetId)
  const best = bestExitNode(venue, graph, dist, {
    targetId,
    targetPoint,
    foreign,
    foreignRings,
    fallbackId: start.nodeId,
  })
  let bestId = best.id
  const bestCost = best.cost
  const edgeExit = bestEdgeExit(
    venue,
    graph,
    dist,
    targetPoint,
    targetLevelOf(venue, targetId),
    profile,
    strict,
    foreign,
    foreignRings,
  )
  let via: LonLat | null = null
  if (edgeExit) {
    // Сравниваем честно: «поворот» тоже штрафуется, если его прямое плечо до
    // цели срезает чужой корпус (иначе он всегда перебивал бы узел со штрафом).
    const exitAim = undrawnEntrancePoint(venue, targetId, edgeExit.via) ?? targetPoint
    const exitCost =
      edgeExit.cost +
      (segmentCrossesRings(edgeExit.via, exitAim, foreignRings) ? THROUGH_BUILDING_PENALTY_M : 0)
    if (exitCost < bestCost) {
      bestId = edgeExit.endpointId
      const endPt = graph.nodes.get(bestId)?.point
      via = endPt && haversineM(endPt, edgeExit.via) > VIA_MIN_M ? edgeExit.via : null
    }
  }
  const nodeIds: string[] = [bestId]
  let cursor = bestId
  while (prev.has(cursor)) {
    cursor = prev.get(cursor) as string
    nodeIds.unshift(cursor)
  }
  return { nodeIds, targetPoint, via }
}

/**
 * Точный маршрут по цепочке прибытия (park-and-walk, R1): для мультимодального
 * «драйв → парковка → пешком к двери» — СОВМЕСТНАЯ оптимизация пары (парковка,
 * дверь) в граф-слое (parkAndWalkRoute), иначе — последовательное разрешение
 * плеч. `walkFromIndex` — индекс узла-транзита (парковки), с которого начинается
 * ПЕШИЙ сегмент (пунктир); -1 — чисто одномодальный маршрут.
 */
function bestDoorRoute(
  venue: Venue,
  graph: BuiltGraph,
  start: RouteStart,
  targetId: string,
  profile: RouteProfile,
): { nodeIds: string[]; walkFromIndex: number } | null {
  const legs = arrivalLegs(venue, targetId, profile)
  if (legs.length === 0) return null
  const strict = strictRouting(venue)
  const [drive, walk] = legs
  // Park-and-walk: драйв к парковкам + пешком к дверям → совместная оптимизация
  // пары (не геометрия из route-access); при недостижимости — фолбэк ниже.
  const isParkAndWalk =
    legs.length === 2 &&
    (drive?.profile === 'vehicle' || drive?.profile === 'truck') &&
    walk?.profile === 'foot'
  if (isParkAndWalk && drive && walk) {
    const pair = parkAndWalkRoute(
      graph,
      start.nodeId,
      drive.nodeIds,
      walk.nodeIds,
      drive.profile,
      strict,
    )
    if (pair) return pair
  }
  // Одномодальные / фолбэк: последовательное разрешение плеч (drive-toward и пр.).
  return sequentialLegs(graph, start.nodeId, legs, strict)
}

/** Пеший сегмент park-and-walk + финальный «дожим» внутрь — единой пунктирной. */
function buildWalkPolyline(walkNodes: readonly LonLat[], egress: readonly LonLat[]): LonLat[] {
  const tail = egress.length > 0 ? [egress[1] as LonLat] : []
  const walkRaw = [...walkNodes, ...tail]
  return walkRaw.length > 1 ? densifyPolyline(dedupeConsecutive(walkRaw), DENSIFY_STEP_M) : []
}

/**
 * Маршрут до цели. Дверь достижима — точный путь по графу; нет — фолбэк
 * «по сети докуда можно + прямое плечо». null только если у площадки нет
 * сети/старта или у цели нет геометрии.
 */
export function planRoute(
  venue: Venue,
  graph: BuiltGraph,
  start: RouteStart,
  targetId: string,
  profile: RouteProfile = 'foot',
): RoutePlan | null {
  const exact = bestDoorRoute(venue, graph, start, targetId, profile)
  const fallback = exact ? null : reachableFallback(venue, graph, start, targetId, profile)
  const nodeIds = exact?.nodeIds ?? fallback?.nodeIds
  if (!nodeIds) return null

  const nodePoints = nodeIds
    .map((id) => graph.nodes.get(id)?.point)
    .filter((p): p is LonLat => p !== undefined)
  // Сход с середины ребра: точка поворота — часть дороги, не пунктира.
  if (fallback?.via) nodePoints.push(fallback.via)

  const lastNode = nodePoints[nodePoints.length - 1]
  // Здание-цель без нарисованной двери: не рисуем сквозь стену — доводим до её
  // края (ближайшая к концу сети точка контура), а не до точки внутри здания.
  const wall = lastNode ? undrawnEntrancePoint(venue, targetId, lastNode) : null
  const endPoint = wall ?? arrivalPoint(venue, targetId, profile) ?? fallback?.targetPoint ?? null

  const raw: LonLat[] = [start.point, ...nodePoints]
  if (endPoint && lastNode && haversineM(endPoint, lastNode) > 1) raw.push(endPoint)

  const polyline = densifyPolyline(dedupeConsecutive(raw), DENSIFY_STEP_M)
  const distanceM = polylineLengthM(polyline)

  // Park-and-walk (R1): drive-часть (SOLID) → пеший «дожим» от парковки (DASHED).
  const wi = exact?.walkFromIndex ?? -1
  const drivePoints = wi >= 0 ? nodePoints.slice(0, wi + 1) : nodePoints
  const walkNodes = wi >= 0 ? nodePoints.slice(wi) : []

  // Сегменты для честной отрисовки: по сети — сплошная, вне дорог — пунктир.
  const firstNode = nodePoints[0]
  const approach: LonLat[] =
    firstNode && haversineM(start.point, firstNode) > OFF_NETWORK_EPS_M
      ? [start.point, firstNode]
      : []
  const egress: LonLat[] =
    endPoint && lastNode && haversineM(endPoint, lastNode) > OFF_NETWORK_EPS_M
      ? [lastNode, endPoint]
      : []
  const networkPolyline = densifyPolyline(dedupeConsecutive(drivePoints), DENSIFY_STEP_M)
  const walkPolyline = buildWalkPolyline(walkNodes, egress)

  return {
    targetId,
    polyline,
    networkPolyline,
    walkPolyline,
    approach,
    egress,
    nodeIds,
    levelSplit: splitByLevel(graph, nodeIds),
    distanceM,
    durationS: distanceM / SPEED_MPS[profile],
    profile,
    start,
  }
}

/** Убирает слипшиеся соседние вершины (источник фантомных поворотов в степпере). */
function dedupeConsecutive(points: readonly LonLat[]): LonLat[] {
  const out: LonLat[] = []
  for (const p of points) {
    const prev = out[out.length - 1]
    if (prev && haversineM(prev, p) < DUPLICATE_POINT_EPS_M) continue
    out.push(p)
  }
  return out
}
